import React, { useMemo, useState } from 'react';
import ModalComponent from '../../../../common-components/ModalComponent/ModalComponent';
import '../../styles/library.scss';
import InputComponent from '../../../../common-components/InputComponent/InputComponent';
import CustomSelect from '../../../inbound/components/CustomSelect';
import ButtonComponent from '../../../../common-components/ButtonComponent/ButtonComponent';
import { useDispatch, useSelector } from 'react-redux';
import LabelItem from '../CampaignDetails/Labelling/LabelItem';
import { useFormValidation } from '../../../../../hooks/useFormValidation';
import { CreateLibraryValidation } from './validation';
import { useApi } from '../../../../../hooks/useApi';
import { createLibrary, editLibrarySettings } from '../../../../../services/postTracking.service';
import { fetchPostTrackingLibraries } from '../../../../../redux/socialListening/socialListeningActions';
import { NotificationManager } from 'react-notifications';

const CreateEditLibraryModal = ({ show, onClose, mode = 'create', library, setLibrary, onSuccess }) => {
  const { labels } = useSelector((s) => s.socialListening);
  const [formValues, setFormValues] = useState(() => {
    if (library) {
      return { title: library.title, labels: labels.filter((l) => library?.labels?.includes(l._id)) };
    }
    return { title: '', labels: [] };
  });
  const dispatch = useDispatch();

  const { triggerFetch: createLibraryMutation, isLoading: isCreatingLibrary } = useApi(createLibrary, {
    type: 'mutation',
    onSuccess: () => {
      NotificationManager.success('New Library Created', '', 1200);
      onClose();
      dispatch(fetchPostTrackingLibraries(1, 9, true));
      onSuccess?.();
    },
    onError: (e) => {
      NotificationManager.error(e?.data?.message || 'Something went wrong', '', 1200);
    },
  });

  const { triggerFetch: editLibraryMutation, isLoading: isEditingLibrary } = useApi(editLibrarySettings, {
    type: 'mutation',
    onSuccess: () => {
      NotificationManager.success('Changes Saved!', '', 1200);
      setLibrary((lib) => ({ ...lib, title: formValues.title, labels: formValues.labels.map((l) => l._id) }));
      onClose();
      dispatch(fetchPostTrackingLibraries(1, 9, true));
      onSuccess?.();
    },
    onError: (e) => {
      NotificationManager.error(e?.data?.message || 'Something went wrong', '', 1200);
    },
  });

  const labelOptions = useMemo(
    () => (labels ? labels.map((l) => ({ value: l._id, label: l.title, originalLabel: l })) : []),
    [labels]
  );

  const { errors, setErrors, validate } = useFormValidation(CreateLibraryValidation);

  const setFieldValue = (key, valueOrFunction) => {
    setFormValues((existingValues) => {
      const newValue = typeof valueOrFunction === 'function' ? valueOrFunction(existingValues[key]) : valueOrFunction;
      return {
        ...existingValues,
        [key]: newValue,
      };
    });
  };

  const renderSelectedOptions = () => {
    const selectedLabels = formValues.labels;
    if (!selectedLabels.length) return 'Select Labels';
    return `${selectedLabels.length} Label${selectedLabels.length > 1 ? '(s)' : ''} selected`;
  };

  const isValidData = useMemo(() => validate(formValues, true), [formValues]);

  const onSubmit = (e) => {
    e?.preventDefault();
    const isValid = validate(formValues);
    if (!isValid) return;

    if (mode === 'create') {
      createLibraryMutation({
        args: {
          title: formValues.title,
          labelIds: formValues.labels.map((l) => l._id),
        },
      });
    } else {
      editLibraryMutation({
        args: {
          title: formValues.title,
          labelIds: formValues.labels.map((l) => l._id),
          libraryId: library._id,
        },
      });
    }
  };

  return (
    <ModalComponent width={452} show={show} onClose={onClose} style={{ padding: 0 }}>
      <form onSubmit={onSubmit} className="sl-create-library-modal-container">
        <div className="sl-create-library-header">{mode === 'create' ? 'Create New Library' : 'Library Settings'}</div>
        <div className="sl-create-library-form">
          <InputComponent
            value={formValues.title}
            setValue={(v) => {
              setErrors({});
              setFieldValue('title', v);
            }}
            label="Library Name"
            required
            placeholder="Enter Name"
            error={errors.title}
          />
          <CustomSelect
            error={errors.labels}
            label="Select Labels That Classify"
            options={labelOptions}
            value={formValues.labels}
            placeholderText="Select Labels"
            renderSelectedLabel={renderSelectedOptions}
            renderOption={({ option }) => {
              const isSelected = Boolean(formValues.labels.find((l) => l._id === option.value));
              return (
                <div key={option.value} style={{ marginBottom: '10px' }}>
                  <LabelItem
                    size="md"
                    color={option.originalLabel.color}
                    title={option.label}
                    onClick={() => {
                      if (isSelected) setFieldValue('labels', (l) => [...l.filter((l) => l._id !== option.value)]);
                      else setFieldValue('labels', (l) => [...l, option.originalLabel]);
                    }}
                    checked={isSelected}
                    hoverable
                  />
                </div>
              );
            }}
            required
            dropDownMaxHeight="250px"
            dropDownContainerStyle={{ padding: '16px' }}
          />
          {Boolean(formValues.labels.length) && (
            <div className="sl-create-library-preview-container">
              {formValues.labels.map((label) => (
                <LabelItem
                  size="md"
                  color={label.color}
                  title={label.title}
                  onRemove={() => setFieldValue('labels', (v) => [...v.filter((l) => l._id !== label._id)])}
                />
              ))}
            </div>
          )}
          <div className="sl-create-library-bottom-container">
            <ButtonComponent
              showLoading={isEditingLibrary || isCreatingLibrary}
              disabled={!isValidData}
              type="submit"
              style={{ width: '100%' }}>
              {mode === 'create' ? 'Create' : 'Save Changes'}
            </ButtonComponent>
          </div>
        </div>
      </form>
    </ModalComponent>
  );
};

export default CreateEditLibraryModal;
